import { formatCamelCaseToSpaces, formatTextToSlug } from "@/utils/helper"
import { SportFilter } from "@/Overtime/enums/markets"
import { orderBy } from "lodash"
import { League } from "overtime-utils"
import { LEAGUES_SORT_PRIORITY } from "../constants/sports"

interface SportDetail {
  id: string
  slug: string
  name: string
  sportId: string
  sporthub: {
    id: string
    slug: string
  }
  activeGamesCount: number
  activeLiveGamesCount: number
  activePrematchGamesCount: number
  leagues: Map<string, any>
}

interface Game {
  gameId: string
  sport: string
  leagueId: number
  leagueName: string
  isOpen: boolean
  live: boolean
  statusCode: string
  tournamentName: string
}

export const transformToNavigationData = (games: any, isLive: boolean) => {
  const sportDetails = new Map()

  // Initialize all sports from SportFilter enum
  Object.values(SportFilter).forEach(sport => {
    // Skip non-sport filters like 'Boosted', 'Live', 'Favourites', 'PlayerProps', 'All'
    if (
      [
        "Boosted",
        "Live",
        "Favourites",
        "PlayerProps",
        "All",
        "Promo",
        "QuickSgp",
      ].includes(sport)
    ) {
      return
    }

    sportDetails.set(sport, {
      id: sport.toLowerCase(),
      slug: formatTextToSlug(formatCamelCaseToSpaces(sport)),
      name: formatCamelCaseToSpaces(sport),
      sportId: sport.toLowerCase(),
      sporthub: {
        id: sport.toLowerCase() === "Esports" ? "Esports" : "sports",
        slug: sport.toLowerCase() === "Esports" ? "Esports" : "sports",
      },
      activeGamesCount: 0,
      activeLiveGamesCount: 0,
      activePrematchGamesCount: 0,
      leagues: new Map(),
    })
  })

  // First pass: collect all tournaments for each league
  const tournamentsByLeague = new Map()
  for (const game of games) {
    const { sport, leagueId, leagueName, tournamentName } = game

    if (!sport || sport.trim() === "") {
      continue
    }

    const leagueKey = `${sport}-${leagueId}`
    if (!tournamentsByLeague.has(leagueKey)) {
      tournamentsByLeague.set(leagueKey, {
        leagueId,
        leagueName,
        tournaments: new Set(),
      })
    }

    if (tournamentName && tournamentName.trim() !== "") {
      tournamentsByLeague.get(leagueKey).tournaments.add(tournamentName)
    }
  }

  // Process games in a single pass
  for (const game of games) {
    const {
      sport,
      leagueId,
      leagueName,
      gameId,
      isOpen,
      live,
      tournamentName,
    } = game

    // Debug logging for empty sport values
    if (!sport || sport.trim() === "") {
      // console.warn('Empty or missing sport value in game:', game)
      continue // Skip games with empty sport values
    }

    // Initialize sport if not exists (for sports not in SportFilter enum)
    if (!sportDetails.has(sport)) {
      sportDetails.set(sport, {
        id: gameId,
        slug: formatTextToSlug(sport),
        name: formatCamelCaseToSpaces(sport),
        sportId: sport.toLowerCase(),
        sporthub: {
          id: sport.toLowerCase() === "Esports" ? "Esports" : "sports",
          slug: sport.toLowerCase() === "Esports" ? "Esports" : "sports",
        },
        activeGamesCount: 0,
        activeLiveGamesCount: 0,
        activePrematchGamesCount: 0,
        leagues: new Map(),
      })
    }

    const sportData = sportDetails.get(sport)
    const leagueKey = `${sport}-${leagueId}`
    const leagueInfo = tournamentsByLeague.get(leagueKey)
    const hasMultipleTournaments = leagueInfo && leagueInfo.tournaments.size > 1

    // Create a unique key for league entry
    let entryKey = leagueId
    let entryName = leagueName

    if (
      hasMultipleTournaments &&
      tournamentName &&
      tournamentName.trim() !== ""
    ) {
      // If there are multiple tournaments, create separate entries for each
      entryKey = `${leagueId}-${tournamentName}`
      entryName = `${leagueName}-${tournamentName}`
    } else if (tournamentName && tournamentName !== leagueName) {
      // Single tournament case - use existing logic
      entryName = `${leagueName} - ${tournamentName}`
    }

    // Initialize league if not exists
    if (!sportData.leagues.has(entryKey)) {
      sportData.leagues.set(entryKey, {
        id: entryKey,
        slug: formatTextToSlug(entryName),
        name: entryName,
        count: 0,
        turnover: "0",
        activeGamesCount: 0,
        activeLiveGamesCount: 0,
        activePrematchGamesCount: 0,
      })
    }

    const leagueData = sportData.leagues.get(entryKey)

    // Update counts - consider both isOpen and live flag
    if (isOpen) {
      leagueData.count++

      if (live) {
        leagueData.activeLiveGamesCount++
        sportData.activeLiveGamesCount++
      } else {
        leagueData.activePrematchGamesCount++
        sportData.activePrematchGamesCount++
      }

      leagueData.activeGamesCount++
      sportData.activeGamesCount++
    }
  }

  // const sortedMarkets = orderBy(
  //   sportDetails,
  //   [
  //     sportDetail => {
  //       const sortPriority = LEAGUES_SORT_PRIORITY.findIndex(
  //         (league: League) => league === sportDetail.id
  //       )
  //       return sortPriority !== -1 ? sortPriority : Number.MAX_VALUE
  //     },
  //   ],
  //   ['asc']
  // ).slice(0, 9)

  // Convert to final format and output
  const result = Array.from(sportDetails.entries())
    .map(([sport, details]: [string, SportDetail]) => {
      const leagues = Array.from(details.leagues.values())
      const total = leagues.reduce((sum, league: any) => sum + league.count, 0)
      // console.log({ details })

      const sportName =
        details.name === SportFilter.Soccer
          ? SportFilter.Football
          : details.name === SportFilter.Football
            ? "American Football"
            : details.name

      const sportSlug =
        details.slug === "soccer"
          ? "football"
          : details.slug === "football"
            ? "american-football"
            : details.slug

      const transformedSport = {
        id: details.id,
        slug: sportSlug,
        name: sportName,
        sportId: details.sportId,
        sporthub: details.sporthub,
        countries: [
          {
            id: `${details.id}_international-tournaments`,
            slug: "international-tournaments",
            name: "International Tournaments",
            turnover: "0",
            activeGamesCount: total,
            activeLiveGamesCount: isLive ? total : 0,
            activePrematchGamesCount: isLive ? 0 : total,
            leagues: leagues.map(league => ({
              id: league.id,
              slug: league.slug,
              name: league.name,
              turnover: league.turnover,
              activeGamesCount: league.activeGamesCount,
              activeLiveGamesCount: league.activeLiveGamesCount,
              activePrematchGamesCount: league.activePrematchGamesCount,
            })),
          },
        ],
        activeLiveGamesCount: details.activeLiveGamesCount,
        activePrematchGamesCount: details.activePrematchGamesCount,
        activeGamesCount: details.activeGamesCount,
      }

      return transformedSport
    })
    .sort((a, b) => b.activeGamesCount - a.activeGamesCount)

  // console.log('Final result:', result)
  return result
}
